import React, { useState } from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaLinkedin, FaMapMarkerAlt, FaRegCheckCircle } from 'react-icons/fa';

const Contact = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulating form submission
    setFormStatus('success');
    // In a real implementation, you would submit the form data to your backend/API here
  };

  return (
    <ContactContainer id="contact">
      <BackgroundElement className="element-1" />
      <BackgroundElement className="element-2" />
      
      <div className="container">
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5 }}
        >
          Get in Touch
        </SectionTitle>
        
        <SectionSubtitle
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Have questions or ready to start your coaching journey? I'm here to help.
        </SectionSubtitle>
        
        <ContactContent ref={ref}>
          <ContactFormWrapper
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {formStatus === 'success' ? (
              <SuccessMessage>
                <SuccessIcon>
                  <FaRegCheckCircle />
                </SuccessIcon>
                <h3>Message Sent Successfully!</h3>
                <p>Thank you for reaching out. I'll get back to you shortly.</p>
                <ResetButton onClick={() => {
                  setFormState({ name: '', email: '', message: '' });
                  setFormStatus(null);
                }}>
                  Send Another Message
                </ResetButton>
              </SuccessMessage>
            ) : (
              <ContactForm onSubmit={handleSubmit}>
                <FormTitle>Send a Message</FormTitle>
                
                <FormGroup>
                  <Label htmlFor="name">Full Name</Label>
                  <Input 
                    type="text" 
                    id="name" 
                    name="name" 
                    placeholder="Your name" 
                    value={formState.name}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="email">Email Address</Label>
                  <Input 
                    type="email" 
                    id="email" 
                    name="email" 
                    placeholder="Your email address" 
                    value={formState.email}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="message">Your Message</Label>
                  <Textarea 
                    id="message" 
                    name="message" 
                    rows="5" 
                    placeholder="How can I help you?" 
                    value={formState.message}
                    onChange={handleChange}
                    required
                  ></Textarea>
                </FormGroup>
                
                <SubmitButton type="submit">
                  Send Message
                </SubmitButton>
              </ContactForm>
            )}
          </ContactFormWrapper>
          
          <ContactInfoWrapper
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <InfoCard>
              <InfoHeader>
                <h3>Contact Information</h3>
                <p>Feel free to reach out through any of these channels:</p>
              </InfoHeader>
              
              <ContactMethodsList>
                <ContactMethod>
                  <ContactIcon>
                    <FaPhone />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>Phone</ContactLabel>
                    <ContactValue>+33 7 85 81 36 80</ContactValue>
                  </ContactText>
                </ContactMethod>
                
                <ContactMethod>
                  <ContactIcon>
                    <FaEnvelope />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>Email</ContactLabel>
                    <ContactValue>info@samanthaknowles.com</ContactValue>
                  </ContactText>
                </ContactMethod>
                
                <ContactMethod>
                  <ContactIcon>
                    <FaLinkedin />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>LinkedIn</ContactLabel>
                    <ContactLink href="https://linkedin.com/in/samanthaknowles" target="_blank" rel="noopener noreferrer">
                      linkedin.com/in/samanthaknowles
                    </ContactLink>
                  </ContactText>
                </ContactMethod>
                
                <ContactMethod>
                  <ContactIcon>
                    <FaMapMarkerAlt />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>Location</ContactLabel>
                    <ContactValue>Offering virtual coaching worldwide and in-person sessions in select locations</ContactValue>
                  </ContactText>
                </ContactMethod>
              </ContactMethodsList>
              
              <InfoFooter>
                <span>Your journey to transformation is just a message away</span>
              </InfoFooter>
            </InfoCard>
          </ContactInfoWrapper>
        </ContactContent>
      </div>
      
      <DiagonalDivider />
    </ContactContainer>
  );
};

const ContactContainer = styled.section`
  background-color: #f9f9f9;
  position: relative;
  overflow: hidden;
  padding: 100px 0 120px;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 20px;
  position: relative;
  color: #1A2A3A;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: #A8C4A0;
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  color: #5A7F8C;
  font-size: 1.2rem;
  max-width: 600px;
  margin: 30px auto 60px;
  line-height: 1.6;
`;

const ContactContent = styled.div`
  display: flex;
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const ContactFormWrapper = styled(motion.div)`
  flex: 1;
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
  position: relative;
  
  @media (max-width: 992px) {
    order: 2;
  }
`;

const ContactForm = styled.form`
  padding: 40px;
`;

const FormTitle = styled.h3`
  color: #1A2A3A;
  font-size: 1.5rem;
  margin-bottom: 30px;
  position: relative;
  padding-bottom: 15px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 2px;
    background-color: #A8C4A0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #1A2A3A;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #A8C4A0;
    box-shadow: 0 0 0 3px rgba(168, 196, 160, 0.2);
  }
  
  &::placeholder {
    color: #aaa;
    font-size: 0.9rem;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
  transition: all 0.3s ease;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #A8C4A0;
    box-shadow: 0 0 0 3px rgba(168, 196, 160, 0.2);
  }
  
  &::placeholder {
    color: #aaa;
    font-size: 0.9rem;
  }
`;

const SubmitButton = styled.button`
  display: inline-block;
  padding: 14px 30px;
  background-color: #A8C4A0;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(168, 196, 160, 0.3);
  
  &:hover {
    background-color: #97b38f;
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(168, 196, 160, 0.4);
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 40px;
  
  h3 {
    color: #1A2A3A;
    font-size: 1.5rem;
    margin: 15px 0;
  }
  
  p {
    color: #666;
    margin-bottom: 30px;
    max-width: 300px;
  }
`;

const SuccessIcon = styled.div`
  width: 70px;
  height: 70px;
  background-color: rgba(168, 196, 160, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #A8C4A0;
  margin-bottom: 15px;
`;

const ResetButton = styled.button`
  display: inline-block;
  padding: 12px 25px;
  background-color: transparent;
  color: #5A7F8C;
  border: 2px solid #5A7F8C;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(90, 127, 140, 0.1);
    transform: translateY(-3px);
  }
`;

const ContactInfoWrapper = styled(motion.div)`
  flex: 1;
  
  @media (max-width: 992px) {
    order: 1;
    margin-bottom: 30px;
  }
`;

const InfoCard = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 150px;
    height: 150px;
    background-color: rgba(168, 196, 160, 0.05);
    border-radius: 0 0 0 100%;
    z-index: 0;
  }
`;

const InfoHeader = styled.div`
  margin-bottom: 30px;
  position: relative;
  z-index: 1;
  
  h3 {
    color: #1A2A3A;
    font-size: 1.5rem;
    margin-bottom: 15px;
    position: relative;
    padding-bottom: 15px;
    
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 40px;
      height: 2px;
      background-color: #A8C4A0;
    }
  }
  
  p {
    color: #666;
    line-height: 1.6;
  }
`;

const ContactMethodsList = styled.div`
  flex: 1;
  position: relative;
  z-index: 1;
`;

const ContactMethod = styled.div`
  display: flex;
  margin-bottom: 25px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactIcon = styled.div`
  width: 46px;
  height: 46px;
  background-color: rgba(90, 127, 140, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: #5A7F8C;
  margin-right: 15px;
  flex-shrink: 0;
`;

const ContactText = styled.div`
  flex: 1;
`;

const ContactLabel = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: #1A2A3A;
  margin-bottom: 5px;
`;

const ContactValue = styled.div`
  color: #666;
  line-height: 1.5;
  font-size: 0.95rem;
`;

const ContactLink = styled.a`
  color: #5A7F8C;
  text-decoration: none;
  line-height: 1.5;
  font-size: 0.95rem;
  transition: color 0.2s ease;
  
  &:hover {
    color: #A8C4A0;
    text-decoration: underline;
  }
`;

const InfoFooter = styled.div`
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  font-style: italic;
  color: #888;
  font-size: 0.9rem;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const BackgroundElement = styled.div`
  position: absolute;
  
  &.element-1 {
    width: 600px;
    height: 600px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v50H50v50H0z' fill='%23A8C4A0' fill-opacity='0.05'/%3E%3C/svg%3E");
    top: -200px;
    right: -200px;
    z-index: 0;
    transform: rotate(-15deg);
  }
  
  &.element-2 {
    width: 500px;
    height: 500px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h50v50H0z' fill='%235A7F8C' fill-opacity='0.03'/%3E%3C/svg%3E");
    bottom: -100px;
    left: -150px;
    z-index: 0;
    transform: rotate(10deg);
  }
`;

const DiagonalDivider = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background-color: white;
  clip-path: polygon(0 100%, 100% 40%, 100% 100%, 0 100%);
  z-index: 1;
`;

export default Contact;